import { useEffect, useMemo, useRef, useState } from "react";
import { VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { PlayerItem } from "../components/player/interfaces";

/** Fallback ratio for interactive sources until the real video size is known. */
export const DEFAULT_INTERACTIVE_RATIO = 32 / 9;

const getMediaId = (item: PlayerItem): string | undefined => item.getData().extra?.mediaSession?.id;

const isInteractive = (item: PlayerItem): boolean =>
  item.getData().extra?.mediaSession?.extra?.playerFit === "interactive";

/** Ratio from config-provided dimensions (source settings), if valid. */
const ratioFromDimensions = (extra: any): number | undefined =>
  extra?.width > 0 && extra?.height > 0 ? extra.width / extra.height : undefined;

/**
 * Shared skeleton of the two ratio hooks: `fallbacks` declares which ids are
 * interactive and their best ratio known without measurement (config dimensions
 * or {@link DEFAULT_INTERACTIVE_RATIO}); the live value is probed from the
 * intrinsic dimensions (`videoWidth`/`videoHeight`) of the <video> element
 * inside each id's container — the single source of truth for both live
 * streams and VODs (remote WebRTC track `getSettings()` is unreliable).
 * Measurements are polled (dimensions only become available once metadata is
 * loaded, and can change on resolution adaptation) and win over the fallback.
 * Every id in `fallbacks` is guaranteed an entry.
 */
const useProbedRatios = (
  fallbacks: Record<string, number>,
  getContainer: (id: string) => HTMLElement | null,
): Record<string, number> => {
  const [measured, setMeasured] = useState<Record<string, number>>({});
  const fallbacksRef = useRef(fallbacks);
  fallbacksRef.current = fallbacks;
  const getContainerRef = useRef(getContainer);
  getContainerRef.current = getContainer;

  useEffect(() => {
    const sample = () => {
      setMeasured((prev) => {
        let next = prev;
        let changed = false;
        Object.keys(fallbacksRef.current).forEach((id) => {
          const video = getContainerRef.current(id)?.querySelector("video");
          if (!video?.videoWidth || !video?.videoHeight) return;
          const ratio = video.videoWidth / video.videoHeight;
          if (Math.abs((prev[id] ?? 0) - ratio) > 0.001) {
            if (!changed) {
              next = { ...prev };
              changed = true;
            }
            next[id] = ratio;
          }
        });
        return changed ? next : prev;
      });
    };
    sample();
    const interval = setInterval(sample, 1000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const result: Record<string, number> = {};
    Object.keys(fallbacks).forEach((id) => {
      result[id] = measured[id] ?? fallbacks[id];
    });
    return result;
  }, [fallbacks, measured]);
};

/**
 * Aspect ratios of the interactive live sources, keyed by mediaSession id.
 * Probed from the <video> element inside each source's container; falls back
 * to the source's config-declared dimensions (`mediaSession.extra.width/height`),
 * then to {@link DEFAULT_INTERACTIVE_RATIO}. Every interactive item has an entry.
 */
export const useInteractiveAspectRatios = (
  items: PlayerItem[] | undefined,
  getContainer: (mediaId: string) => HTMLElement | null,
): Record<string, number> => {
  const fallbacks = useMemo(() => {
    const result: Record<string, number> = {};
    (items ?? []).forEach((item) => {
      if (!isInteractive(item)) return;
      const id = getMediaId(item);
      if (!id) return;
      result[id] = ratioFromDimensions(item.getData().extra?.mediaSession?.extra) ?? DEFAULT_INTERACTIVE_RATIO;
    });
    return result;
  }, [items]);

  return useProbedRatios(fallbacks, getContainer);
};

/**
 * VOD variant of {@link useInteractiveAspectRatios}: filters the interactive
 * VODs itself (`extra.playerFit`, frozen at record time) and returns their
 * ratios keyed by vod id — so `id in result` doubles as the interactive flag.
 * Fallback dimensions come from the VOD's `extra` (frozen from the live
 * source's config), then {@link DEFAULT_INTERACTIVE_RATIO}.
 */
export const useInteractiveVodAspectRatios = (
  vods: VodOutputDTO[],
  getContainer: (vodId: string) => HTMLElement | null,
): Record<string, number> => {
  const fallbacks = useMemo(() => {
    const result: Record<string, number> = {};
    vods.forEach((v) => {
      // `extra.mediaExtra` = the live media's `extra`, frozen at record time
      // (the backend's `extra` is a wider bag the recording chain writes into).
      const mediaExtra = v.extra?.mediaExtra;
      if (mediaExtra?.playerFit !== "interactive") return;
      result[v.id] = ratioFromDimensions(mediaExtra) ?? DEFAULT_INTERACTIVE_RATIO;
    });
    return result;
  }, [vods]);

  return useProbedRatios(fallbacks, getContainer);
};
